import type { GlobalThemeOverrides } from 'naive-ui'

/**
 * Dashboard theme — modelled directly on Yummy Admin's `common/theme/theme-overrides.ts`.
 * Values are documented in docs/v2/tokens.md §1.
 */
export const dashboardTheme: GlobalThemeOverrides = {
  common: {
    fontFamily: '"Inter var", ui-sans-serif, system-ui, sans-serif',
    primaryColor: '#00ad4c',
    primaryColorHover: '#00c456',
    primaryColorPressed: '#009642',
    errorColor: '#FF0055',
    warningColor: '#FF8000',
    infoColor: '#2080f0',
    successColor: '#00ad4c',
    borderRadius: '5px',
    borderRadiusSmall: '3px',
    borderColor: '#e4e7ec',
    // Naive's tertiary text (#767c82) is 4.22:1 on white -- under AA. `n-text depth="3"`, the
    // muted lines on descriptions and cards all read it. 5.4:1 (Phase 13, axe on the profile page).
    textColor3: '#5f6b7a',
  },
  Card: { borderRadius: '7px' },
  // A count badge is content when it is the only place the number appears (responsive.md §5).
  Badge: { fontSize: '14px' },
  Tag: {
    borderRadius: '4px',
    // Naive's small tag is 12px; every status tag in the app is small and is content, so it
    // takes the 14px floor (responsive.md §5, Phase 13 responsive sweep).
    fontSizeSmall: '14px',
    // Tag text is small and meaningful, so it takes the accessible variants (tokens.scss) —
    // the same values, which since 2026-09-23 clear 4.5:1 on the tag's own tint over the
    // darker light panel as well as over white.
    textColorSuccess: '#007332',
    textColorError: '#bb003d',
    textColorInfo: '#1861b6',
    // Matches --apmp-warning-text; see tokens.scss for why it is this dark.
    textColorWarning: '#a04f00',
  },
  List: { borderRadius: '0', borderColorPopover: '#e4e7ec' },
  Notification: { padding: '15px' },
  Menu: {
    // Naive's group heading is #c2c2c2 on white — 1.7:1, which axe reports as a serious
    // contrast violation the moment a sidebar is grouped (Phase 03). A heading that labels
    // the navigation is content, not decoration, so it takes the accessible muted token.
    groupTextColor: '#4b5563',
    itemTextColorActive: '#007d36',
    itemTextColorActiveHover: '#007d36',
    itemTextColorChildActive: '#007d36',
    itemTextColorChildActiveHover: '#007d36',
    itemColorHover: 'rgba(189, 189, 189, 0.15)',
  },
  Spin: {
    // Naive dims whatever a spinner covers to 50% while it loads. Body text (#333639) becomes
    // #999b9c on white — 2.79:1 — so every label, heading and empty state under a loading panel
    // is below AA for as long as the fetch takes, and axe reports it whenever a scan lands
    // mid-fetch. The spinner is what says "loading"; the dimming only makes the text it covers
    // unreadable, so it is turned off rather than tuned to a value that happens to pass for
    // one colour (Phase 05).
    opacitySpinning: '1',
  },
  Steps: {
    // Naive paints every step that is not the current one in `textColorDisabled` (#c2c2c2 —
    // 1.78:1 on white), titles included. A wizard's step titles are the map of the task: the
    // one you have finished and the one you are going to are both content, not decoration,
    // and axe reports each of them as a serious violation (Phase 05). They take the same
    // accessible muted token as the sidebar's group headings.
    headerTextColorWait: '#4b5563',
    headerTextColorFinish: '#4b5563',
    descriptionTextColorWait: '#4b5563',
    descriptionTextColorFinish: '#4b5563',
  },
  Form: {
    // `feedbackTextColor` is Naive's `textColor3` (#767c82), 4.22:1 on white — just under AA,
    // and the feedback line is where a field explains itself ("Already filled in from what you
    // told us"). Serious axe violation on the wizard's first step (Phase 05).
    feedbackTextColor: '#4b5563',
  },
  Tabs: {
    // The active tab's label is the primary green, which is 2.97:1 on white — a serious
    // contrast failure, and the label of the tab you are on is the least optional text on
    // the page. The accessible green (--apmp-link, 6.00:1 on white, 5.50:1 on the light panel)
    // is used for the text; the primary green stays
    // on the underline, where it is a 3:1 UI boundary rather than 4.5:1 text.
    tabTextColorActiveLine: '#007332',
    tabTextColorHoverLine: '#007332',
    tabTextColorActiveBar: '#007332',
    tabTextColorHoverBar: '#007332',
    tabTextColorActiveCard: '#007332',
    // A disabled tab is still read: it tells the officer a section exists and is not ready.
    // Naive's default disabled text is ~2.4:1 on the page background, which the axe gate
    // flags as serious; the muted token (the same value the menu group headings use, 7.5:1
    // on white) keeps it visibly secondary and legible.
    tabTextColorDisabledLine: '#4b5563',
    tabTextColorDisabledBar: '#4b5563',
    tabTextColorDisabledCard: '#4b5563',
    tabTextColorDisabledSegment: '#4b5563',
  },
  // Phase 07: a timeline's time stamp is content on the tenancy and payment pages, not
  // decoration. Naive's default is 12px at 4.22:1 on white; both fail the gates.
  Timeline: { metaTextColor: '#4b5563' },
}

export const dashboardDarkTheme: GlobalThemeOverrides = {
  common: {
    fontFamily: '"Inter var", ui-sans-serif, system-ui, sans-serif',
    primaryColor: '#00ad4c',
    errorColor: '#FF0055',
    warningColor: '#FF8000',
    borderRadius: '5px',
    borderRadiusSmall: '3px',
    borderColor: '#4b556eff',
    cardColor: '#202c4633',
    popoverColor: '#0f172a',
    modalColor: '#1c202c',
    // The same tertiary text on the dark surfaces: 7:1 on #1c202c.
    textColor3: '#a3adbd',
    // Naive's dark placeholder is `rgba(255, 255, 255, 0.38)` -- 2.9:1 on its own input fill,
    // which is what every search box, select and textarea in the app asks the user to read
    // before they have typed anything. axe does not test placeholder text, so this survived
    // the accessibility gate; it is still the least readable text on a dark screen. 5.2:1.
    placeholderColor: 'rgba(255, 255, 255, 0.62)',
  },
  Card: { borderRadius: '7px', color: '#1c202c' },
  Badge: { fontSize: '14px' },
  // Naive's dark theme paints the tick in `cardColor`, assuming that is the opaque surface
  // behind the box. Ours is `#202c4633` — 20% alpha — so the tick was drawn at a fifth of its
  // opacity on the green fill and all but disappeared on a selected row. An opaque near-black
  // is 6.3:1 on the primary green; white would be 3:1, which is why the dark mark is right.
  Checkbox: { checkMarkColor: '#101828' },
  Dropdown: { color: '#1c2334' },
  Drawer: { color: '#1c202c' },
  DataTable: {
    thColor: '#1c202c',
    tdColor: '#1c2334',
    hoverColor: '#1c202c',
    tdColorHover: '#1c202c',
  },
  Tag: { borderRadius: '4px', fontSizeSmall: '14px' },
  List: { borderRadius: '0', borderColorPopover: '#1c2334', colorHoverPopover: '#1c202c' },
  Menu: {
    groupTextColor: '#9ca3af',
    itemColorHover: 'rgba(189, 189, 189, 0.15)',
    itemTextColorActive: '#2fbf6b',
    itemTextColorActiveHover: '#2fbf6b',
    itemTextColorChildActive: '#2fbf6b',
    itemTextColorChildActiveHover: '#2fbf6b',
  },
  Spin: { opacitySpinning: '1' },
  // The same two repairs on the dark surfaces, where Naive's disabled grey is even weaker.
  Steps: {
    headerTextColorWait: '#9ca3af',
    headerTextColorFinish: '#9ca3af',
    descriptionTextColorWait: '#9ca3af',
    descriptionTextColorFinish: '#9ca3af',
    // The NUMBER inside a step that has not been reached yet takes `textColorDisabled`
    // (rgba(255,255,255,0.38), 3.35:1 on the wizard's card). It is the step's label -- "2" is
    // how the user refers to it -- so it takes the same muted token as the title beside it,
    // and the ring around it comes with it or the number floats unattached.
    indicatorTextColorWait: '#9ca3af',
    indicatorBorderColorWait: '#9ca3af',
  },
  Form: {
    feedbackTextColor: '#9ca3af',
    // The required asterisk is `errorColor` (#FF0055), 4.3:1 on the dark form surface. It is
    // the only mark that says a field is mandatory, so it takes the dark error TEXT variant.
    asteriskColor: '#ff719a',
  },
  // On the dark surfaces the primary green is 5.48:1 and the light-theme green would be
  // 3.08:1 — the reverse of the light theme, so the values swap rather than being shared.
  Tabs: {
    tabTextColorActiveLine: '#2fbf6b',
    tabTextColorHoverLine: '#2fbf6b',
    tabTextColorActiveBar: '#2fbf6b',
    tabTextColorHoverBar: '#2fbf6b',
    tabTextColorActiveCard: '#2fbf6b',
    tabTextColorDisabledLine: '#9ca3af',
    tabTextColorDisabledBar: '#9ca3af',
    tabTextColorDisabledCard: '#9ca3af',
    tabTextColorDisabledSegment: '#9ca3af',
  },
  Timeline: { metaTextColor: '#9ca3af' },
}
